import type { QuoteRate, RateQuoteResponse } from "@/lib/api/types";

const RECENT_QUOTES_KEY = "shippeek-recent-quotes-v1";
const RECENT_DRAFTS_KEY = "shippeek-recent-quote-drafts-v1";
const MAX_RECENT_ITEMS = 20;

export type QuoteMode = "ltl" | "parcel";

type RecentQuoteRate = {
  ref: string;
  carrier: string;
  carrierCode: string;
  serviceType: string;
  serviceDescription: string;
  total: number;
  days: number | null;
};

type RecentQuoteSnapshot = {
  refId: string;
  mode: QuoteMode;
  createdAt: string;
  rates: RecentQuoteRate[];
};

type QuoteDraftSnapshot = {
  id: string;
  mode: QuoteMode;
  createdAt: string;
  originAddress: string;
  originCity: string;
  originState: string;
  originPostalCode: string;
  destAddress: string;
  destCity: string;
  destState: string;
  destPostalCode: string;
  weight: string;
  freightClass: string;
  handlingUnits: string;
};

function parseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function toRecentRate(rate: QuoteRate): RecentQuoteRate {
  return {
    ref: rate.ref,
    carrier: rate.carrier,
    carrierCode: rate.carrierCode,
    serviceType: rate.serviceType,
    serviceDescription: rate.serviceDescription,
    total: rate.total,
    days: rate.days ?? null,
  };
}

function normalizeRefId(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

export function readRecentQuotes(): RecentQuoteSnapshot[] {
  if (!isBrowser()) return [];
  const parsed = parseJson<RecentQuoteSnapshot[]>(window.localStorage.getItem(RECENT_QUOTES_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((item) => item && item.refId && item.mode && item.createdAt);
}

export function findRecentQuote(refId: string): RecentQuoteSnapshot | null {
  const normalizedRef = normalizeRefId(refId).toLowerCase();
  if (!normalizedRef) return null;
  return (
    readRecentQuotes().find((item) => item.refId.toLowerCase() === normalizedRef) ??
    null
  );
}

export function saveRecentQuote(params: {
  mode: QuoteMode;
  response: RateQuoteResponse;
  overrideRefId?: string;
}): void {
  if (!isBrowser()) return;

  const refId = normalizeRefId(params.overrideRefId ?? params.response.refId ?? params.response.id);
  if (!refId) return;

  const rates = (params.response.rates ?? []).map(toRecentRate);
  const next: RecentQuoteSnapshot = {
    refId,
    mode: params.mode,
    createdAt: new Date().toISOString(),
    rates,
  };

  const current = readRecentQuotes();
  const deduped = current.filter((item) => item.refId.toLowerCase() !== refId.toLowerCase());
  window.localStorage.setItem(
    RECENT_QUOTES_KEY,
    JSON.stringify([next, ...deduped].slice(0, MAX_RECENT_ITEMS)),
  );
}

export function readQuoteDrafts(): QuoteDraftSnapshot[] {
  if (!isBrowser()) return [];
  const parsed = parseJson<QuoteDraftSnapshot[]>(window.localStorage.getItem(RECENT_DRAFTS_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((item) => item && item.id && item.createdAt && item.mode);
}

export function saveQuoteDraft(draft: Omit<QuoteDraftSnapshot, "id" | "createdAt">): QuoteDraftSnapshot {
  const snapshot: QuoteDraftSnapshot = {
    ...draft,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  };

  if (!isBrowser()) return snapshot;

  const current = readQuoteDrafts();
  window.localStorage.setItem(
    RECENT_DRAFTS_KEY,
    JSON.stringify([snapshot, ...current].slice(0, MAX_RECENT_ITEMS)),
  );

  return snapshot;
}
